import { useCallback, useTransition } from 'react'
import { create } from 'zustand'
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'

import { replaceUrlIfChanged } from '../../common/helpers/replace-url-if-changed'
import type { MenuFilterCategory, SelectedMenuFilterCategory } from '../domain/menu-category-filter'
import type { MenuItemFilter } from '../domain/menu-item-filter'

export const urlParamStorage: StateStorage = {
  getItem: (_key): string | null => {
    if (typeof window === 'undefined') return null

    const searchParams = new URLSearchParams(window.location.search)

    const categoryParam = searchParams.get('category')
    const goalsParam = searchParams.get('goals')
    const allergensParam = searchParams.get('allergens')
    const dietariesParam = searchParams.get('dietaries')
    const itemNameParam = searchParams.get('itemName')

    const state = {
      itemName: itemNameParam || '',
      selectedCategories: [],
      goals: [],
      allergens: [],
      dietaries: [],
      initialised: false,
      _urlParams: {
        categoryParam,
        goalsParam,
        allergensParam,
        dietariesParam,
      },
    }

    return JSON.stringify({ state, version: 0 })
  },

  setItem: (_key, newValue): void => {
    if (typeof window === 'undefined' || !newValue) return

    try {
      const { state } = JSON.parse(newValue)

      // Prevent URL updates during initialization/hydration
      if (!state.initialised) {
        return
      }

      const searchParams = new URLSearchParams(window.location.search)

      // Clear existing filter params
      searchParams.delete('itemName')
      searchParams.delete('category')
      searchParams.delete('goals')
      searchParams.delete('allergens')
      searchParams.delete('dietaries')

      // Set individual parameters cleanly
      if (state.itemName) {
        searchParams.set('itemName', state.itemName)
      }

      // Handle categories - use comma-separated values to match old logic
      if (state.selectedCategories?.length) {
        const categoryValues = state.selectedCategories
          .map((category: MenuFilterCategory | SelectedMenuFilterCategory | string) => {
            if (typeof category === 'string') return category
            return category.name || category.id
          })
          .filter(Boolean)

        if (categoryValues.length) {
          searchParams.set('category', categoryValues.join(','))
        }
      }

      // Handle filter arrays - use comma-separated values to match old logic
      if (state.goals?.length) {
        const goalsValue = state.goals.filter(Boolean).join(',')
        if (goalsValue) searchParams.set('goals', goalsValue)
      }

      if (state.allergens?.length) {
        const allergensValue = state.allergens.filter(Boolean).join(',')
        if (allergensValue) searchParams.set('allergens', allergensValue)
      }

      if (state.dietaries?.length) {
        const dietariesValue = state.dietaries.filter(Boolean).join(',')
        if (dietariesValue) searchParams.set('dietaries', dietariesValue)
      }

      replaceUrlIfChanged(searchParams)
    } catch (error) {
      console.error('Failed to parse state for URL storage:', error)
    }
  },

  removeItem: (_key): void => {
    if (typeof window === 'undefined') return
    const searchParams = new URLSearchParams(window.location.search)

    searchParams.delete('itemName')
    searchParams.delete('category')
    searchParams.delete('goals')
    searchParams.delete('allergens')
    searchParams.delete('dietaries')

    replaceUrlIfChanged(searchParams)
  },
}

type GmFiltersStore = {
  itemName: string
  selectedCategories: SelectedMenuFilterCategory[]
  goals: MenuItemFilter[]
  allergens: MenuItemFilter[]
  dietaries: MenuItemFilter[]
  initialised: boolean
  setItemName: (itemName: string) => void
  setSelectedCategories: (categories: MenuFilterCategory[]) => void
  unsetSelectedCategory: () => void
  setDietaryPreferences: (
    type: 'allergens' | 'dietaries' | 'goals',
    dietaryPreferences: MenuItemFilter[],
  ) => void
  clearFilters: () => void
  initialiseFilters: ({
    itemName,
    categories,
    goals,
    dietaries,
    allergens,
  }: {
    itemName: string
    categories: MenuFilterCategory[] | SelectedMenuFilterCategory[]
    goals: MenuItemFilter[]
    dietaries: MenuItemFilter[]
    allergens: MenuItemFilter[]
  }) => void
}

const useGmFiltersStore = create<GmFiltersStore>()(
  persist(
    (set, _get) => {
      return {
        itemName: '',
        selectedCategories: [],
        selectedGoals: [],
        goals: [],
        allergens: [],
        dietaries: [],
        initialised: false,
        setItemName: (itemName) => {
          set({ itemName })
        },
        setSelectedCategories: (selectedCategories) => set({ selectedCategories }),
        unsetSelectedCategory: () => set({ selectedCategories: [] }),
        setDietaryPreferences: (type, selected) =>
          set({
            [type]: selected,
          }),
        clearFilters: () => {
          set({
            itemName: '',
            selectedCategories: [],
            goals: [],
            allergens: [],
            dietaries: [],
            initialised: true,
          })
        },
        initialiseFilters: ({ itemName, categories, goals, dietaries, allergens }) => {
          set({
            itemName,
            selectedCategories: categories,
            goals,
            allergens,
            dietaries,
            initialised: true,
          })
        },
      }
    },
    {
      name: 'gm-filters-storage',
      storage: createJSONStorage(() => urlParamStorage),
      partialize: (state) => ({
        itemName: state.itemName,
        selectedCategories: state.selectedCategories,
        initialised: state.initialised,
        // Store only the values for URL serialization - the full objects will be reconstructed by initialiseFilters
        goals: state.goals.map((filter) => filter.value),
        allergens: state.allergens.map((filter) => filter.value),
        dietaries: state.dietaries.map((filter) => filter.value),
      }),
      onRehydrateStorage: () => (state) => {
        // When store is hydrated from URL, we need to convert string values back to full filter objects
        // This will be handled by the component that calls initialiseFilters with the proper filter objects
        if (state && !state.initialised) {
          // Mark that we need to initialize filters from URL values
          state.initialised = false
        }
      },
    },
  ),
)

export type { GmFiltersStore }

export const useGmFiltersState = () =>
  useGmFiltersStore(
    useShallow((state) => ({
      itemName: state.itemName,
      selectedCategories: state.selectedCategories,
      goals: state.goals,
      allergens: state.allergens,
      dietaries: state.dietaries,
      initialised: state.initialised,
    })),
  )

export const useHasActiveFilters = () =>
  useGmFiltersStore(
    (state) =>
      state.itemName.length > 0 ||
      state.selectedCategories.length > 0 ||
      state.goals.length > 0 ||
      state.allergens.length > 0 ||
      state.dietaries.length > 0,
  )

export const useGmFiltersActions = () =>
  useGmFiltersStore(
    useShallow((state) => ({
      setItemName: state.setItemName,
      setSelectedCategories: state.setSelectedCategories,
      unsetSelectedCategory: state.unsetSelectedCategory,
      setDietaryPreferences: state.setDietaryPreferences,
      clearFilters: state.clearFilters,
      initialiseFilters: state.initialiseFilters,
    })),
  )

/**
 * Wrap filter actions with React's startTransition.
 * This marks filter updates as non-urgent, allowing React to:
 * - Keep the UI responsive during expensive re-renders
 * - Interrupt rendering if higher priority updates come in
 */
export const useGmFiltersActionsWithTransition = () => {
  const [isPending, startTransition] = useTransition()
  const actions = useGmFiltersStore(
    useShallow((state) => ({
      setItemName: state.setItemName,
      setSelectedCategories: state.setSelectedCategories,
      unsetSelectedCategory: state.unsetSelectedCategory,
      setDietaryPreferences: state.setDietaryPreferences,
      clearFilters: state.clearFilters,
      initialiseFilters: state.initialiseFilters,
    })),
  )

  const clearFiltersWithTransition = useCallback(() => {
    startTransition(() => {
      actions.clearFilters()
    })
  }, [actions, startTransition])

  const setItemNameWithTransition = useCallback(
    (itemName: string) => {
      startTransition(() => {
        actions.setItemName(itemName)
      })
    },
    [actions, startTransition],
  )

  const setSelectedCategoriesWithTransition = useCallback(
    (categories: MenuFilterCategory[]) => {
      startTransition(() => {
        actions.setSelectedCategories(categories)
      })
    },
    [actions, startTransition],
  )

  const setDietaryPreferencesWithTransition = useCallback(
    (type: 'allergens' | 'dietaries' | 'goals', preferences: MenuItemFilter[]) => {
      startTransition(() => {
        actions.setDietaryPreferences(type, preferences)
      })
    },
    [actions, startTransition],
  )

  return {
    ...actions,
    clearFilters: clearFiltersWithTransition,
    setItemName: setItemNameWithTransition,
    setSelectedCategories: setSelectedCategoriesWithTransition,
    setDietaryPreferences: setDietaryPreferencesWithTransition,
    isPending,
  }
}
